import {
  Badge,
  Button,
  Card,
  Modal,
  Section,
  Textarea,
  useToast,
} from '@memorizecards/ui';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CardHtml } from '../components/CardHtml';
import { Comentarios } from '../components/Comentarios';
import {
  IconChat,
  IconClock,
  IconFlag,
  IconNotebook,
  IconSpeaker,
  IconStar,
  IconStop,
} from '../components/Icons';
import { SeletorAssunto } from '../components/SeletorAssunto';
import { Carregando, Erro, Vazio } from '../components/estado';
import { formatClock, formatNumber, labelDoCaminho } from '../lib/format';
import {
  useAnswerCard,
  useReportar,
  useSalvarNota,
  useStudySession,
  useToggleFavorita,
} from '../lib/queries';
import { textoSimples } from '../lib/sanitize';
import type { ReviewGrade, StudyCard, StudySession } from '../lib/types';
import { useFocusTimer } from '../lib/useFocusTimer';
import { type VozGenero, useSpeech } from '../lib/useSpeech';

const RESPOSTAS: Array<{
  grade: ReviewGrade;
  rotulo: string;
  atalho: string;
  chave: keyof StudySession['projections'];
  classe: string;
}> = [
  {
    grade: 1,
    rotulo: 'Errei',
    atalho: '1',
    chave: 'errei',
    classe: 'border-mc-danger/50 bg-mc-danger/10 text-mc-danger hover:bg-mc-danger/20',
  },
  {
    grade: 2,
    rotulo: 'Difícil',
    atalho: '2',
    chave: 'dificil',
    classe:
      'border-mc-warning/50 bg-mc-warning/10 text-mc-warning hover:bg-mc-warning/20',
  },
  {
    grade: 3,
    rotulo: 'Bom',
    atalho: '3',
    chave: 'bom',
    classe: 'border-mc-accent/50 bg-mc-accent/10 text-mc-accent hover:bg-mc-accent/20',
  },
  {
    grade: 4,
    rotulo: 'Fácil',
    atalho: '4',
    chave: 'facil',
    classe:
      'border-[color:color-mix(in_srgb,var(--mc-btn-soft)_50%,transparent)] bg-[color:color-mix(in_srgb,var(--mc-btn-soft)_10%,transparent)] text-[color:var(--mc-btn-soft)] hover:bg-[color:color-mix(in_srgb,var(--mc-btn-soft)_20%,transparent)]',
  },
];

export function Estudar(): ReactNode {
  const [params, setParams] = useSearchParams();
  const toast = useToast();

  const deckId = Number(params.get('deck') ?? 0);
  const subtopic = params.get('assunto');
  const modoRevisao = params.get('revisao') === '1';
  const janelaDias = Number(params.get('janela') ?? 0);
  const somenteFavoritas = params.get('favoritas') === '1';
  const pacote = params.get('pacote');
  const desde = params.get('desde');
  const ate = params.get('ate');
  const ultimasErradas = params.get('erradas') === '1';

  const sessao = useStudySession(
    somenteFavoritas || deckId > 0
      ? {
          deckId: deckId > 0 ? deckId : undefined,
          subtopic,
          modoRevisao,
          janelaDias,
          somenteFavoritas,
          pacote,
          desde,
          ate,
          ultimasErradas,
        }
      : null,
  );
  const responder = useAnswerCard();
  const alternarFavorita = useToggleFavorita();
  const salvarNota = useSalvarNota();
  const reportar = useReportar();
  const fala = useSpeech();

  const [mostrarVerso, setMostrarVerso] = useState(false);
  const [seletorAberto, setSeletorAberto] = useState(false);
  const [notaAberta, setNotaAberta] = useState(false);
  const [comentariosAbertos, setComentariosAbertos] = useState(false);
  const [rascunhoNota, setRascunhoNota] = useState('');
  const [voz, setVoz] = useState<VozGenero>(lerVozGuardada);
  const [favoritaLocal, setFavoritaLocal] = useState<boolean | null>(null);

  const cronometro = useFocusTimer('revisao', somenteFavoritas || deckId > 0);

  const card = sessao.data?.card ?? null;

  // Cada card novo recomeça na frente, sem nota aberta e sem áudio a tocar.
  useEffect(() => {
    setMostrarVerso(false);
    setFavoritaLocal(null);
    setRascunhoNota(card?.nota ?? '');
    fala.parar();
    // `fala` muda de identidade a cada render; depender dele reiniciaria isto
    // sem parar, por isso a dependência é só o card.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [card?.id]);

  const responderCard = useCallback(
    async (grade: ReviewGrade) => {
      if (!card || responder.isPending) return;
      try {
        await responder.mutateAsync({ cardId: card.id, resposta: grade });
        await sessao.refetch();
      } catch (error) {
        toast.error(
          error instanceof Error ? error.message : 'Não consegui salvar a resposta.',
        );
      }
    },
    [card, responder, sessao, toast],
  );

  // Atalhos de teclado: espaço revela, 1–4 respondem. É o que torna uma sessão
  // de duzentos cards suportável sem tirar a mão do teclado.
  useEffect(() => {
    function aoTeclar(e: KeyboardEvent) {
      if (notaAberta || comentariosAbertos || seletorAberto) return;

      const alvo = e.target as HTMLElement | null;
      if (
        alvo &&
        (alvo.tagName === 'INPUT' ||
          alvo.tagName === 'TEXTAREA' ||
          alvo.isContentEditable)
      ) {
        return;
      }
      if (!card) return;

      if (e.key === ' ' || e.key === 'Enter') {
        e.preventDefault();
        setMostrarVerso(true);
        return;
      }
      if (!mostrarVerso) return;

      const resposta = RESPOSTAS.find((r) => r.atalho === e.key);
      if (resposta) {
        e.preventDefault();
        void responderCard(resposta.grade);
      }
    }

    window.addEventListener('keydown', aoTeclar);
    return () => window.removeEventListener('keydown', aoTeclar);
  }, [card, mostrarVerso, responderCard, notaAberta, comentariosAbertos, seletorAberto]);

  const alternarParametro = useCallback(
    (chave: string, valor: string | null) => {
      const proximos = new URLSearchParams(params);
      if (valor === null) proximos.delete(chave);
      else proximos.set(chave, valor);
      setParams(proximos, { replace: true });
    },
    [params, setParams],
  );

  const textoParaLer = useMemo(() => {
    if (!card) return '';
    return mostrarVerso
      ? textoSimples(card.verso)
      : textoSimples(card.pergunta || card.frente);
  }, [card, mostrarVerso]);

  if (!somenteFavoritas && deckId <= 0) {
    return (
      <Section spacing="md">
        <Vazio
          titulo="Escolha uma matéria"
          descricao="Volte aos flashcards e clique em Estudar na matéria que quer revisar."
          acao={
            <Link to="/baralhos">
              <Button>Ver flashcards</Button>
            </Link>
          }
        />
      </Section>
    );
  }

  if (sessao.isPending) return <Carregando className="py-24" />;
  if (sessao.error) {
    return (
      <Section spacing="md">
        <Erro error={sessao.error} aoTentarNovamente={() => void sessao.refetch()} />
      </Section>
    );
  }

  const dados = sessao.data!;
  const favorita = favoritaLocal ?? card?.favorita ?? false;

  return (
    <Section spacing="sm">
      <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="mc-mono mc-eyebrow mb-1.5 break-words">
            {dados.deck.pacote ?? 'Matéria'}
          </p>
          <h1 className="break-words text-mc-h3">{dados.deck.nome}</h1>
          {dados.subtopic ? (
            <p className="mt-1.5 text-sm text-mc-ink2">
              {labelDoCaminho(dados.subtopic)}
            </p>
          ) : null}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <span
            className="inline-flex min-h-[38px] items-center gap-2 rounded-mc-pill border border-mc-line2 px-3 text-sm tabular-nums text-mc-ink2"
            title={
              cronometro.ativo
                ? 'Contando o tempo de estudo'
                : 'Pausado — a contagem volta quando você interagir'
            }
          >
            <IconClock
              className={`size-4 ${cronometro.ativo ? 'text-mc-accent' : 'text-mc-ink3'}`}
            />
            {formatClock(cronometro.segundos)}
          </span>

          <Button variant="ghost" size="sm" onClick={() => setSeletorAberto(true)}>
            {dados.subtopic ? 'Trocar assunto' : 'Filtrar assunto'}
          </Button>

          <Button
            variant={modoRevisao ? 'primary' : 'ghost'}
            size="sm"
            onClick={() => alternarParametro('revisao', modoRevisao ? null : '1')}
            aria-pressed={modoRevisao}
            title="Só cards que já foram estudados e estão vencendo"
          >
            Revisão inteligente
          </Button>
        </div>
      </header>

      <div className="mb-6 flex flex-wrap gap-2">
        <Contador
          rotulo="Novos"
          valor={`${formatNumber(dados.counts.novosDisponiveisHoje)} de ${formatNumber(dados.counts.novosPorDia)}`}
        />
        <Contador rotulo="Aprendendo" valor={dados.counts.aprendizado} tom="warning" />
        <Contador rotulo="Revisar" valor={dados.counts.revisar} tom="accent" />
        <Contador rotulo="Feitos hoje" valor={dados.counts.concluidosHoje} />
      </div>

      {card === null ? (
        dados.proximoEmAprendizagem ? (
          <AguardandoAprendizagem
            proximo={dados.proximoEmAprendizagem}
            aoExpirar={() => void sessao.refetch()}
          />
        ) : (
          <SessaoConcluida
            modoRevisao={modoRevisao}
            janelaDias={janelaDias}
            aoAmpliarJanela={() =>
              alternarParametro('janela', String(Math.min(30, janelaDias + 3)))
            }
          />
        )
      ) : (
        <>
          <Card padding="lg" className="relative min-w-0">
            {card.caminho.length > 0 ? (
              <p className="mc-mono mb-4 break-words normal-case tracking-normal">
                {card.caminho.slice(2).join(' › ') || card.caminho.join(' › ')}
              </p>
            ) : null}

            <div className="min-h-[8rem] min-w-0 break-words">
              <CardHtml html={card.pergunta || card.frente} className="text-lg" />
            </div>

            {mostrarVerso ? (
              <>
                <hr className="mc-rule my-7" />
                <div className="min-w-0 overflow-x-auto sm:overflow-x-visible">
                  <CardHtml html={card.verso} />
                </div>
              </>
            ) : null}

            <div className="mt-7 flex flex-wrap items-center gap-2 border-t border-mc-line pt-5">
              <BotaoIcone
                rotulo={favorita ? 'Remover das favoritas' : 'Marcar como favorita'}
                ativo={favorita}
                onClick={() => {
                  setFavoritaLocal(!favorita);
                  alternarFavorita.mutate(
                    { cardId: card.id },
                    {
                      onSuccess: (r) => setFavoritaLocal(r.favorito),
                      onError: () => {
                        setFavoritaLocal(favorita);
                        toast.error('Não consegui salvar a favorita.');
                      },
                    },
                  );
                }}
              >
                <IconStar className="size-4" />
              </BotaoIcone>

              <BotaoIcone
                rotulo="Minha anotação"
                ativo={Boolean(card.nota)}
                onClick={() => setNotaAberta(true)}
              >
                <IconNotebook className="size-4" />
              </BotaoIcone>

              <BotaoIcone
                rotulo="Comentários"
                onClick={() => setComentariosAbertos(true)}
              >
                <IconChat className="size-4" />
              </BotaoIcone>

              <BotaoIcone
                rotulo={fala.falando ? 'Parar leitura' : 'Ouvir'}
                ativo={fala.falando}
                onClick={() => {
                  if (fala.falando) {
                    fala.parar();
                    return;
                  }
                  void fala.falar({
                    texto: textoParaLer,
                    genero: voz,
                    cardId: card.id,
                    parte: mostrarVerso ? 'back' : 'front',
                  });
                }}
              >
                {fala.falando ? (
                  <IconStop className="size-4" />
                ) : (
                  <IconSpeaker className="size-4" />
                )}
              </BotaoIcone>

              <button
                type="button"
                onClick={() => {
                  const proxima: VozGenero = voz === 'F' ? 'M' : 'F';
                  setVoz(proxima);
                  localStorage.setItem('mc-voz', proxima);
                }}
                className="mc-mono min-h-[38px] rounded-mc-pill border border-mc-line2 px-3 text-mc-ink3 hover:text-mc-ink"
              >
                Voz {voz === 'F' ? 'feminina' : 'masculina'}
              </button>

              <BotaoIcone
                rotulo="Reportar problema neste card"
                onClick={() =>
                  reportar.mutate(
                    { cardId: card.id },
                    {
                      onSuccess: (r) =>
                        toast.success(
                          r.jaReportado
                            ? 'Você já tinha reportado este card.'
                            : 'Obrigado. Vamos revisar este card.',
                        ),
                      onError: () => toast.error('Não consegui registrar o report.'),
                    },
                  )
                }
                className="ml-auto"
              >
                <IconFlag className="size-4" />
              </BotaoIcone>
            </div>
          </Card>

          <div className="mt-6">
            {mostrarVerso ? (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                {RESPOSTAS.map((r) => (
                  <button
                    key={r.grade}
                    type="button"
                    onClick={() => void responderCard(r.grade)}
                    disabled={responder.isPending}
                    className={`mc-btn flex min-h-[64px] flex-col items-center justify-center gap-0.5 rounded-mc-card border font-semibold transition-all duration-200 disabled:opacity-50 ${r.classe}`}
                  >
                    <span>{r.rotulo}</span>
                    <span className="mc-mono text-current opacity-70">
                      {dados.projections[r.chave]}
                    </span>
                  </button>
                ))}
              </div>
            ) : (
              <Button size="lg" fullWidth onClick={() => setMostrarVerso(true)}>
                Mostrar resposta
              </Button>
            )}
            <p className="mc-mono mt-3 text-center">
              {mostrarVerso
                ? 'Teclas 1 a 4 para responder'
                : 'Barra de espaço para revelar'}
            </p>
          </div>
        </>
      )}

      <SeletorAssunto
        aberto={seletorAberto}
        deckId={deckId}
        deckNome={dados.deck.nome}
        selecionado={dados.subtopic}
        aoFechar={() => setSeletorAberto(false)}
        aoEscolher={(path) => alternarParametro('assunto', path)}
      />

      {card ? (
        <>
          <Modal
            open={notaAberta}
            onClose={() => setNotaAberta(false)}
            title="Minha anotação"
            description="Só você vê esta nota. Ela fica ligada a este card."
            footer={
              <>
                <Button variant="ghost" onClick={() => setNotaAberta(false)}>
                  Fechar
                </Button>
                <Button
                  loading={salvarNota.isPending}
                  onClick={() =>
                    salvarNota.mutate(
                      { cardId: card.id, texto: rascunhoNota },
                      {
                        onSuccess: () => {
                          toast.success(
                            rascunhoNota.trim() ? 'Anotação salva.' : 'Anotação removida.',
                          );
                          setNotaAberta(false);
                          void sessao.refetch();
                        },
                        onError: () => toast.error('Não consegui salvar a anotação.'),
                      },
                    )
                  }
                >
                  Salvar
                </Button>
              </>
            }
          >
            <Textarea
              label="Anotação"
              value={rascunhoNota}
              onChange={(e) => setRascunhoNota(e.target.value)}
              placeholder="Escreva o macete, a pegadinha, a base legal…"
              className="min-h-40"
              maxLength={8000}
              autoFocus
            />
          </Modal>

          <Modal
            open={comentariosAbertos}
            onClose={() => setComentariosAbertos(false)}
            title="Comentários"
            description="Visíveis para todos os alunos."
            size="lg"
          >
            <Comentarios cardId={card.id} />
          </Modal>
        </>
      ) : null}
    </Section>
  );
}

function Contador({
  rotulo,
  valor,
  tom = 'neutro',
}: {
  rotulo: string;
  valor: number | string;
  tom?: 'neutro' | 'accent' | 'warning';
}): ReactNode {
  const vazio = valor === 0 || valor === '0 de 0';
  return (
    <span className="inline-flex items-center gap-2 rounded-mc-pill border border-mc-line2 px-3 py-1.5">
      <span className="mc-mono">{rotulo}</span>
      <span
        className={`text-sm font-semibold tabular-nums ${
          vazio
            ? 'text-mc-ink3'
            : tom === 'accent'
              ? 'text-mc-accent'
              : tom === 'warning'
                ? 'text-mc-warning'
                : 'text-mc-ink'
        }`}
      >
        {typeof valor === 'number' ? formatNumber(valor) : valor}
      </span>
    </span>
  );
}

function BotaoIcone({
  rotulo,
  ativo = false,
  onClick,
  className,
  children,
}: {
  rotulo: string;
  ativo?: boolean;
  onClick: () => void;
  className?: string;
  children: ReactNode;
}): ReactNode {
  return (
    <button
      type="button"
      onClick={onClick}
      title={rotulo}
      aria-label={rotulo}
      aria-pressed={ativo}
      className={`grid size-11 place-items-center rounded-mc-pill border transition-colors ${
        ativo
          ? 'border-mc-accent bg-mc-accent/10 text-mc-accent'
          : 'border-mc-line2 text-mc-ink3 hover:text-mc-ink'
      } ${className ?? ''}`}
    >
      {children}
    </button>
  );
}

function AguardandoAprendizagem({
  proximo,
  aoExpirar,
}: {
  proximo: { emSegundos: number; total: number };
  aoExpirar: () => void;
}): ReactNode {
  const [restante, setRestante] = useState(proximo.emSegundos);

  useEffect(() => {
    setRestante(proximo.emSegundos);
  }, [proximo.emSegundos]);

  useEffect(() => {
    if (restante <= 0) {
      aoExpirar();
      return;
    }
    const id = window.setTimeout(() => setRestante((s) => s - 1), 1000);
    return () => window.clearTimeout(id);
  }, [restante, aoExpirar]);

  const mm = String(Math.floor(restante / 60)).padStart(2, '0');
  const ss = String(restante % 60).padStart(2, '0');

  return (
    <Card padding="lg" className="text-center">
      <p className="font-mc-display text-mc-h3 text-mc-warning">
        Volte em {mm}:{ss}
      </p>
      <p className="mx-auto mt-3 max-w-md text-mc-body text-mc-ink2">
        Você tem {formatNumber(proximo.total)} card{proximo.total === 1 ? '' : 's'} em
        aprendizagem aguardando revisão.
      </p>
    </Card>
  );
}

function SessaoConcluida({
  modoRevisao,
  janelaDias,
  aoAmpliarJanela,
}: {
  modoRevisao: boolean;
  janelaDias: number;
  aoAmpliarJanela: () => void;
}): ReactNode {
  return (
    <Card padding="lg" className="text-center">
      <p className="font-mc-display text-mc-h3 text-mc-accent">Tudo em dia por aqui</p>
      <p className="mx-auto mt-3 max-w-md text-mc-body text-mc-ink2">
        {modoRevisao
          ? 'Nenhuma revisão vencendo nesta janela. Você pode antecipar as próximas.'
          : 'Você respondeu todos os cards disponíveis desta seleção. Volte amanhã ou escolha outra matéria.'}
      </p>
      <div className="mt-7 flex flex-wrap justify-center gap-3">
        {modoRevisao && janelaDias < 30 ? (
          <Button variant="ghost" onClick={aoAmpliarJanela}>
            Antecipar mais 3 dias
          </Button>
        ) : null}
        <Link to="/baralhos">
          <Button>Escolher outra matéria</Button>
        </Link>
        <Link to="/desempenho">
          <Button variant="ghost">Ver meu desempenho</Button>
        </Link>
      </div>
    </Card>
  );
}

function lerVozGuardada(): VozGenero {
  return localStorage.getItem('mc-voz') === 'M' ? 'M' : 'F';
}
